<template>
  <div class="calendar-modern">
    <div class="calendar-container">
      <div class="calendar-section">
        <div class="calendar-card">
          <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
            <div class="calendar-scroll">
              <FullCalendar ref="fullCalendar" :options="calendarOptions" />
            </div>
          </b-overlay>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";

import dayGridPlugin from "@fullcalendar/daygrid";

import ptBrLocale from "@fullcalendar/core/locales/pt-br";

export default {
  name: "Schedule",

  components: {
    FullCalendar,
  },

  data() {
    return {
      loading: false,

      calendarOptions: {
        plugins: [dayGridPlugin],

        initialView: "dayGridMonth",

        locale: "pt-br",

        locales: [ptBrLocale],

        datesSet: this.takingDate,

        selectedMonth: null,

        selectedYear: null,

        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth",
        },

        buttonText: {
          today: "Hoje",
          month: "Mês",
          week: "Semana",
          list: "Lista",
        },

        eventContent: null,

        events: [],

        eventClick: this.cardClicked,

        eventTimeFormat: {
          hour: "2-digit",
          minute: "2-digit",
          meridiem: false,
          hour12: false,
        },

        height: "auto",

        aspectRatio: 1.35,

        expandRows: true,

        dayMaxEvents: false,

        moreLinkClick: "popover",

        eventDisplay: "block",

        dayHeaderFormat: { weekday: "long" },

        titleFormat: { year: "numeric", month: "long" },
      },
    };
  },

  props: {
    schedules: {
      type: Array,
      default: () => [],
    },

    month: Number,

    year: Number,
  },

  watch: {
    month(newMonth) {
      this.goMonthYear(newMonth, this.year);
    },

    year(newYear) {
      this.goMonthYear(this.month, newYear);
    },

    schedules: {
      handler() {
        this.mapSchedules();
      },
    },
  },

  async created() {
    this.calendarOptions.eventContent = this.eventContent;

    await this.mapSchedules();
  },

  methods: {
    async mapSchedules() {
      try {
        const events = this.schedules.map((schedule) => ({
          start: schedule.date,
          schedule,
          allDay: true,
          className: `event-${this.getEventType(schedule.schedule_type_name)}`,
        }));

        this.calendarOptions.events = events;

        if (this.$refs.fullCalendar) {
          const calendarApi = this.$refs.fullCalendar.getApi();
          calendarApi.removeAllEvents();
          calendarApi.addEventSource(events);
        }
      } catch (error) {
        console.error(error);
      }
    },

    getEventType(typeName) {
      const type = typeName.toLowerCase();
      if (type.includes("consulta")) return "consultation";
      if (type.includes("exame")) return "exam";
      if (type.includes("medicamento") || type.includes("medicação"))
        return "medication";
      if (type.includes("fisioterapia")) return "therapy";
      if (type.includes("visita")) return "visit";
      return "default";
    },

    eventContent(arg) {
      let {
        event: {
          extendedProps: {
            schedule: {
              resident_name,
              schedule_type_name,
              time_init,
              time_end,
            },
          },
        },
      } = arg;

      const timeDisplay =
        time_init && time_end ? `${time_init} - ${time_end}` : "";

      return {
        html: `
          <div class="event-content">
            <div class="event-type">
              <i class="fas fa-circle event-indicator"></i>
              ${schedule_type_name}
            </div>
            <div class="event-resident">${resident_name}</div>
            ${timeDisplay ? `<div class="event-time">${timeDisplay}</div>` : ""}
          </div>
        `,
      };
    },

    // Luiz pesquisei na documentação é essa é a melhor forma de pegar o Mês selecionado no calendario, não achei um metodo nativo do FulCalendar para isso
    takingDate() {
      if (this.$refs.fullCalendar) {
        const calendarApi = this.$refs.fullCalendar.getApi();

        const currentDate = calendarApi.getDate();

        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();

        // Luiz esse if verifica se o mes selecionado mudou para disparar o evento
        if (this.selectedMonth != month || this.selectedYear != year) {
          this.$emit("selectedMonth", month, year);
        }

        this.selectedMonth = month;
        this.selectedYear = year;
      }
    },

    cardClicked(info) {
      this.$emit("update", info.event.extendedProps.schedule.id);
    },

    goMonthYear(month, year) {
      if (month && year && this.$refs.fullCalendar) {
        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.gotoDate(`${year}-${String(month).padStart(2, "0")}-01`);
      }
    },
  },
};
</script>

<style scoped>
.calendar-modern {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 0;
}

.calendar-container {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* Header Section */
.calendar-header {
  background: white;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.header-content {
  text-align: center;
  max-width: 1400px;
  margin: 0 auto;
}

.calendar-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
  font-size: 24px;
}

.calendar-subtitle {
  color: #64748b;
  margin: 8px 0 0 0;
  font-size: 16px;
  font-weight: 500;
}

/* Calendar Section */
.calendar-section {
  flex: 1;
  padding: 24px 32px 32px 32px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.calendar-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.calendar-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  max-height: 100%;
}

.loading-overlay {
  border-radius: 16px;
  height: 100%;
}

/* Responsive Design */
@media (max-width: 768px) {
  .calendar-header {
    padding: 20px 16px;
  }

  .calendar-section {
    padding: 16px;
  }

  .calendar-title {
    font-size: 22px;
    flex-direction: column;
    gap: 8px;
  }

  .calendar-subtitle {
    font-size: 14px;
  }

  .title-icon {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .calendar-header {
    padding: 16px 12px;
  }

  .calendar-section {
    padding: 12px;
  }

  .calendar-title {
    font-size: 20px;
  }

  .calendar-card {
    border-radius: 12px;
  }
}
</style>

<!-- FullCalendar Global Styles -->
<style>
/* Calendar Container */
.fc {
  font-size: 14px;
  height: 100% !important;
  padding: 24px;
}

/* Toolbar Styling */
.fc .fc-toolbar {
  background: #f8fafc;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.fc .fc-toolbar-title {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
}

/* Buttons Styling */
.fc .fc-button-primary {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%) !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(15, 101, 176, 0.3) !important;
  transition: all 0.3s ease !important;
}

.fc .fc-button-primary:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(15, 101, 176, 0.4) !important;
}

.fc .fc-button-primary:active,
.fc .fc-button-primary:focus {
  background: linear-gradient(135deg, #1e40af 0%, #0f65b0 100%) !important;
  box-shadow: 0 2px 8px rgba(15, 101, 176, 0.4) !important;
}

.fc .fc-button-primary:disabled {
  background: #e5e7eb !important;
  color: #9ca3af !important;
  box-shadow: none !important;
  transform: none !important;
}

/* Calendar Grid */
.fc .fc-view-harness {
  flex: 1 !important;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.fc .fc-scrollgrid {
  border: none !important;
}

/* Header Cells */
.fc .fc-col-header-cell {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  border: 1px solid #e5e7eb !important;
  padding: 16px 8px !important;
}

.fc .fc-col-header-cell-cushion {
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  text-decoration: none !important;
}

/* Day Cells */
.fc .fc-daygrid-day {
  border: 1px solid #f1f5f9 !important;
  min-height: 120px !important;
  position: relative;
}

.fc .fc-daygrid-day:hover {
  background: #f8fafc !important;
}

.fc .fc-daygrid-day-number {
  color: #374151 !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  padding: 8px !important;
  text-decoration: none !important;
  display: block !important;
}

.fc .fc-day-today {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%) !important;
}

.fc .fc-day-today .fc-daygrid-day-number {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  color: white !important;
  border-radius: 50% !important;
  width: 32px !important;
  height: 32px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 4px !important;
  font-weight: 600 !important;
}

.fc .fc-day-other .fc-daygrid-day-number {
  color: #9ca3af !important;
}

/* Events Styling */
.fc .fc-event {
  border: none !important;
  border-radius: 6px !important;
  margin: 2px 4px !important;
  padding: 4px 6px !important;
  font-size: 11px !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.fc .fc-event:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Event Types */
/*
.fc .event-consultation {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
}

.fc .event-exam {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
}

.fc .event-medication {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
}

.fc .event-therapy {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
  color: white !important;
}

.fc .event-visit {
  background: linear-gradient(135deg, #ec4899 0%, #db2777 100%) !important;
  color: white !important;
}
*/

.fc .event-default {
  /* background: linear-gradient(135deg, #64748b 0%, #475569 100%) !important; */
  color: white !important;
}

/* Event Content */
.event-content {
  line-height: 1.2 !important;
}

.event-type {
  font-weight: 600 !important;
  margin-bottom: 2px !important;
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
}

.event-indicator {
  font-size: 6px !important;
}

.event-resident {
  font-weight: 500 !important;
  opacity: 0.95 !important;
  margin-bottom: 1px !important;
}

.event-time {
  font-size: 10px !important;
  opacity: 0.8 !important;
  font-weight: 400 !important;
}

/* More Link */
.fc .fc-more-link {
  color: #0f65b0 !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  padding: 2px 6px !important;
  border-radius: 4px !important;
  background: rgba(15, 101, 176, 0.1) !important;
  text-decoration: none !important;
  transition: all 0.3s ease !important;
}

.fc .fc-more-link:hover {
  background: rgba(15, 101, 176, 0.2) !important;
}

/* Popover */
.fc .fc-popover {
  border: none !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  background: white !important;
}

.fc .fc-popover-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  border-bottom: 1px solid #e5e7eb !important;
  border-radius: 12px 12px 0 0 !important;
  padding: 12px 16px !important;
}

.fc .fc-popover-title {
  color: #1e293b !important;
  font-weight: 600 !important;
  font-size: 14px !important;
}

.fc .fc-popover-close {
  color: #64748b !important;
  font-size: 16px !important;
  cursor: pointer !important;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .fc {
    padding: 16px !important;
    font-size: 12px !important;
  }

  .fc .fc-toolbar {
    padding: 12px 16px !important;
    flex-direction: column !important;
    gap: 12px !important;
  }

  .fc .fc-toolbar-title {
    font-size: 18px !important;
    order: 1 !important;
  }

  .fc .fc-toolbar-chunk {
    display: flex !important;
    justify-content: center !important;
  }

  .fc .fc-button-primary {
    padding: 6px 12px !important;
    font-size: 11px !important;
  }

  .fc .fc-col-header-cell {
    padding: 12px 4px !important;
  }

  .fc .fc-col-header-cell-cushion {
    font-size: 11px !important;
  }

  .fc .fc-daygrid-day {
    min-height: 80px !important;
  }

  .fc .fc-daygrid-day-number {
    font-size: 12px !important;
    padding: 6px !important;
  }

  .fc .fc-day-today .fc-daygrid-day-number {
    width: 28px !important;
    height: 28px !important;
  }

  .fc .fc-event {
    font-size: 10px !important;
    margin: 1px 2px !important;
    padding: 3px 4px !important;
  }

  .event-time {
    display: none !important;
  }
}

@media (max-width: 480px) {
  .fc {
    padding: 12px !important;
  }

  .fc .fc-toolbar {
    padding: 8px 12px !important;
  }

  .fc .fc-toolbar-title {
    font-size: 16px !important;
  }

  .fc .fc-daygrid-day {
    min-height: 60px !important;
  }

  .fc .fc-col-header-cell-cushion {
    font-size: 10px !important;
  }

  .fc .fc-event {
    font-size: 9px !important;
    padding: 2px 3px !important;
  }

  .event-resident {
    display: none !important;
  }
}
</style>